// @ts-strict-ignore
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  Badge,
  Box,
  Overlay,
  Spinner,
  Text,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';
import { useQueryClient } from '@tanstack/react-query';

import { UserContext } from '~/dashboard/context';
import { useEntity, useEntityPii } from '~/entities/hooks';
import apiCalls from '~/helpers/apiCalls';
import { formatTimestamp } from '~/helpers/formatting';
import { useModal } from '~/journey/ModalContext';

import { MatchDetail, MatchStrengthBadge } from './components';
import { ResolutionModal } from './components/ResolutionModal';
import WatchlistTableView from './components/Tables/WatchlistTableView';
import {
  filterMatches, getDoMatchesMatch, getFilterOptions, mergeAiRecommendations,
} from './helpers';
import { useGetWatchlistMatches, useGetAIWatchlistResolver } from './hooks/getWatchlistMatches';
import type {
  EntityData,
  EntityPiiData,
  FilterState,
  MatchData,
  ResolutionPayload,
  TabOptions,
  WatchlistNavigationState,
} from './types';
import { WatchlistWrapper } from './styles';

type WatchlistReviewProps = {
  canAgentResolve?: boolean;
  entityToken: string;
  evaluationToken: string;
  showLatestDecisions?: boolean;
  hideIfNoMatches?: boolean;
};

const initialNavigationState: WatchlistNavigationState = { handleNext: null, handlePrevious: null, navigationLabel: '' };

const { watchlists: { postResolutions } } = apiCalls;

const WatchlistReview = ({
  canAgentResolve = false,
  entityToken,
  evaluationToken,
  showLatestDecisions = false,
  hideIfNoMatches = false,
}: WatchlistReviewProps) => {
  /* -- view state -- */
  const [match, setMatch] = useState<MatchData>(null);
  const [selectedRows, setSelectedRows] = useState({});
  const { open, close } = useModal();

  /* -- data requests -- */
  const queryClient = useQueryClient();
  const { typhonDomain, featureFlags } = useContext(UserContext);
  const addSnackBar = useSnackBar();

  const [{
    data: { matches: rawMatches = [], service = '' },
    isLoading: isLoadingMatches,
    refetch,
  }] = useGetWatchlistMatches({
    evaluations: [{
      entityToken,
      evaluationToken,
    }],
    showLatestDecisions,
    typhonDomain,
  });

  const { data: aiResponse } = useGetAIWatchlistResolver({
    evaluationToken,
    typhonDomain,
    enabled: Boolean(featureFlags?.llmWatchlistAgent),
  });

  const matches = useMemo(
    () => mergeAiRecommendations(rawMatches, aiResponse),
    [rawMatches, aiResponse],
  );

  useEffect(() => {
    if (match) {
      const newMatchRecord = matches.find(
        searchedMatch => (getDoMatchesMatch(match, searchedMatch)),
      );
      setMatch(newMatchRecord);
    }
  }, [matches, match]);

  const {
    data: { entity } = {},
    isLoading: isLoadingEntity,
  }: { data: { entity?: EntityData }; isLoading: boolean } = useEntity({ entityToken });

  const {
    data: entityPii,
    isLoading: isLoadingEntityPii,
  }: { data: EntityPiiData; isLoading: boolean } = useEntityPii({ entityToken });

  const handleResolve = useCallback(async (payload: ResolutionPayload) => {
    const matchPluralized = payload.vendor_entity_identifiers.length === 1 ? 'match' : 'matches';
    try {
      await postResolutions({
        entityToken,
        evaluationToken,
        payload,
        typhonDomain,
      });
      const successMessage = `Successfully resolved ${matchPluralized}`;
      addSnackBar({ title: successMessage, type: 'success' });
      queryClient.invalidateQueries(['watchlist-review', evaluationToken]);
      close();
      setSelectedRows({});
      refetch();
    } catch (error) {
      const errorMessage = `Something went wrong when resolving ${matchPluralized}`;
      if (error.responseError?.minor_code === 4000) {
        // const multipleMatches = '1 or more decisions were';
        // const singleMatch = 'This decision was';
        // addSnackBar({ title: `${payload.vendor_entity_identifiers.length === 1 ? singleMatch : multipleMatches} recorded in Alloy, but an error was returned when recording the decision with ${payload.service_name}.`, type: 'warning' });
        // TEMPORARY OVERRIDE WHILE Wathlist Overrides are in progress - OPERATE1-1150
        addSnackBar({ title: `An error was returned when recording the decision with ${payload.service_name}.`, type: 'danger' });
      } else {
        addSnackBar({ title: errorMessage, type: 'danger' });
      }

      close();
    }
  }, [addSnackBar, entityToken, evaluationToken, typhonDomain, close, queryClient, refetch]);

  /* -- filtering state and logic -- */
  const filterOptions = useMemo(() => getFilterOptions(matches), [matches]);
  const {
    fieldOptions,
    typeOptions,
    statusOptions,
    strengthOptions,
    suppressedOptions,
    screeningTypeOptions,
    serviceOptions,
    aiRecommendationOptions,
  } = filterOptions;
  const [allFilters, setAllFilters] = useState<FilterState>({
    selectedFields: [fieldOptions[0]],
    selectedLastUpdated: null,
    selectedStatuses: [statusOptions[0]],
    selectedStrengths: [strengthOptions[0]],
    selectedSuppressed: suppressedOptions[0],
    selectedTypes: [typeOptions[0]],
    selectedScreeningTypes: [screeningTypeOptions[0]],
    selectedServices: [serviceOptions[0]],
    selectedAiRecommendations: [aiRecommendationOptions[0]],
  });
  const [reviewFilters, setReviewFilters] = useState<FilterState>({
    selectedFields: [fieldOptions[0]],
    selectedLastUpdated: null,
    selectedTypes: [typeOptions[0]],
    selectedStrengths: [strengthOptions[0]],
    selectedScreeningTypes: [screeningTypeOptions[0]],
    selectedServices: [serviceOptions[0]],
    selectedAiRecommendations: [aiRecommendationOptions[0]],
  });

  const reviewMatches = useMemo(() => (
    matches.filter(({ status, is_suppressed }) => (status === 'Needs Review' && !is_suppressed))
  ), [matches]);
  const filteredAllMatches = useMemo(() => (
    filterMatches({ matches, filterState: allFilters, service })
  ), [matches, allFilters, service]);
  const filteredReviewMatches = useMemo(() => (
    filterMatches({ matches: reviewMatches, filterState: reviewFilters, service })
  ), [reviewMatches, reviewFilters, service]);

  /* -- control the initial tab state -- */
  const [tab, setTab] = useState<TabOptions>('all');
  /** Use this to only switch to 'review' the first time, not after user choses to navigate to 'all' */
  const prevReviewCountRef = useRef(0);
  // When data first loads with review matches, default to the review tab; when review becomes empty, switch to 'all'
  useEffect(() => {
    const hasReviewMatches = reviewMatches.length > 0;
    if (hasReviewMatches && prevReviewCountRef.current === 0) {
      setTab('review');
    } else if (!hasReviewMatches && tab !== 'all') {
      setTab('all');
    }
    prevReviewCountRef.current = reviewMatches.length;
    // Don't include tab in deps list to avoid hitting the first condition every time tab is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reviewMatches,
  ]);

  /* -- match detail navigation logic -- */
  const currentTabMatches = useMemo(
    () => (tab === 'all' ? filteredAllMatches : filteredReviewMatches),
    [tab, filteredAllMatches, filteredReviewMatches],
  );

  const currentMatchIndex = useMemo(
    () => (
      currentTabMatches.findIndex(
        searchedMatch => getDoMatchesMatch(searchedMatch, match),
      )
    ), [currentTabMatches, match]);

  const [
    { handleNext, handlePrevious, navigationLabel },
    setNavigationState,
  ] = useState<WatchlistNavigationState>(initialNavigationState);

  useEffect(() => {
    // if no match, then we're on the table view and can reset the state
    if (!match) {
      setNavigationState(initialNavigationState);
      return;
    }

    if (match && currentMatchIndex === -1) {
      // if a match is selected but not in currentTabMatches, then it's been resolved
      // and we should preserve the nav state until the next triggered update
      return;
    }

    const label = `${currentMatchIndex + 1}/${currentTabMatches.length}`;
    const nextMatch = currentTabMatches[currentMatchIndex + 1];
    const prevMatch = currentTabMatches[currentMatchIndex - 1];

    setNavigationState({
      handleNext: currentMatchIndex === currentTabMatches.length - 1
        ? null
        : () => setMatch(nextMatch),
      handlePrevious: currentMatchIndex === 0
        ? null
        : () => setMatch(prevMatch),
      navigationLabel: label,
    });
  }, [currentTabMatches, currentMatchIndex, match]);

  /* -- modal logic -- */
  const openModal = useCallback(
    (selectedMatches: MatchData[], options?: { skipPrefill?: boolean }) => {
      open(
        <ResolutionModal
          closeModal={close}
          handleResolve={handleResolve}
          selectedMatches={selectedMatches}
          service={service}
          skipPrefill={options?.skipPrefill}
        />,
        { isComponentLibraryModal: true },
      );
    },
    [
      close,
      handleResolve,
      open,
      service,
    ],
  );

  const changeTab = (newTab: TabOptions) => {
    setSelectedRows({});
    setTab(newTab);
  };

  if (isLoadingEntityPii || isLoadingEntity || isLoadingMatches) {
    return (
      <Box justifyContent="center" style={{ padding: 'var(--cl-space-2)' }}>
        <Spinner />
      </Box>
    );
  }

  if (hideIfNoMatches && !matches.length) {
    return (
      <Box style={{ marginTop: 'var(--cl-space-5)' }}>
        <Text
          as="span"
          className="light-grey"
        >
          <em>No watch list matches found</em>
        </Text>
      </Box>
    );
  }

  return (
    <WatchlistWrapper
      alignItems="flex-start"
      className="watchlist-switch-wrapper"
      id="watchlist-review-container"
    >
      {match
        ? (
          <Overlay
            id="watchlist-match-detail-container"
            topHeader={(
              <>
                <Box style={{ maxWidth: 'fit-content' }}>
                  <Text size={2} weight={700}>{match.name}</Text>

                  <MatchStrengthBadge service={service} strength={match?.match_strength} />
                </Box>
                <Box direction="row" justifyContent="flex-end" style={{ maxWidth: 'fit-content' }} wrap="wrap">
                  <Badge status="neutral">{match.status}</Badge>
                  {match.is_suppressed && (
                    <Tooltip
                      content={match.resolution.timestamp ? `Suppressed on ${formatTimestamp(match.resolution.timestamp, 'MM/DD/YY')}` : 'Suppressed'}
                      side="top"
                    >
                      <Badge status="neutral">Suppressed</Badge>
                    </Tooltip>
                  )}
                </Box>
              </>
            )}
            styleOverrides={{ container: { height: '100%', position: 'static' } }}
            backButton={{
              text: 'Back',
              onClick: () => {
                datadogRum.addAction('watchlistDetail-back');
                return setMatch(null);
              },
            }}
          >
            <MatchDetail
              service={service}
              enableResolution={canAgentResolve}
              entityPii={entityPii}
              entity={entity}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
              match={match}
              navigationLabel={navigationLabel}
              openModal={openModal}
            />
          </Overlay>
        )
        : (
          <WatchlistTableView
            allFilters={allFilters}
            allMatches={matches}
            enableResolution={canAgentResolve}
            filteredAllMatches={filteredAllMatches}
            filterOptions={filterOptions}
            filteredReviewMatches={filteredReviewMatches}
            handleResolve={handleResolve}
            onRowClick={setMatch}
            openModal={openModal}
            reviewFilters={reviewFilters}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            setAllFilters={setAllFilters}
            setReviewFilters={setReviewFilters}
            setTab={changeTab}
            tab={tab}
            totalReviewCount={reviewMatches.length}
          />
        )}
    </WatchlistWrapper>
  );
};

export default WatchlistReview;
